import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { cn } from '../../../shared/utils/cn';
import type { StepIndex } from './use-wizard';
import { STEP_TITLES, TOTAL_STEPS } from './use-wizard';

interface ProgressIndicatorProps {
  currentStep: StepIndex;
}

export function ProgressIndicator({ currentStep }: ProgressIndicatorProps) {
  const stepIndexes: StepIndex[] = [0, 1, 2, 3];
  // The rail spans between the first and last circle centres, so progress is
  // measured against (TOTAL_STEPS - 1) gaps rather than steps.
  const railProgress = currentStep === 0 ? 0 : (currentStep / (TOTAL_STEPS - 1)) * 100;
  const mobileProgress = ((currentStep + 1) / TOTAL_STEPS) * 100;

  return (
    <nav aria-label="Wizard progress" className="mb-2">
      <ol className="hidden md:grid grid-cols-4 relative list-none p-0 m-0">
        <div
          className="absolute left-[12.5%] right-[12.5%] top-[17px] h-[2px] bg-hilevel-border rounded-full"
          aria-hidden="true"
        />
        <div
          className="absolute left-[12.5%] top-[17px] h-[2px] rounded-full bg-gradient-to-r from-hilevel-red to-[#b8172d] transition-[width] duration-500 ease-out"
          style={{ width: `calc((100% - 25%) * ${railProgress / 100})` }}
          aria-hidden="true"
        />

        {stepIndexes.map((step) => {
          const isCurrent = step === currentStep;
          const isDone = step < currentStep;
          let circleStateClass = 'bg-white text-hilevel-gray border border-hilevel-border';
          if (isCurrent) {
            circleStateClass =
              'bg-hilevel-red text-white shadow-[0_0_0_4px_rgba(219,30,54,0.18)] scale-110';
          } else if (isDone) {
            circleStateClass = 'bg-hilevel-red text-white';
          }
          const titleStateClass = isCurrent || isDone ? 'text-hilevel-slate' : 'text-hilevel-gray';
          return (
            <li
              key={step}
              className="relative flex flex-col items-center gap-2"
              aria-current={isCurrent ? 'step' : undefined}
            >
              <span
                className={cn(
                  'relative z-10 inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition-all duration-300',
                  circleStateClass,
                )}
              >
                {isDone ? (
                  <FontAwesomeIcon icon={faCheck} className="text-xs" aria-hidden="true" />
                ) : (
                  step + 1
                )}
              </span>
              <span
                className={cn(
                  'text-xs lg:text-sm font-medium tracking-tight text-center px-1 leading-tight transition-colors duration-300',
                  titleStateClass,
                )}
              >
                {STEP_TITLES[step]}
              </span>
            </li>
          );
        })}
      </ol>

      <div className="md:hidden">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-xs uppercase tracking-wider font-semibold text-hilevel-gray">
            Step {currentStep + 1} of {TOTAL_STEPS}
          </span>
          <span className="text-sm font-semibold text-hilevel-slate">
            {STEP_TITLES[currentStep]}
          </span>
        </div>
        <div
          className="h-1.5 bg-hilevel-light rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={currentStep + 1}
          aria-valuemin={1}
          aria-valuemax={TOTAL_STEPS}
        >
          <div
            className="h-full rounded-full bg-gradient-to-r from-hilevel-red to-[#b8172d] transition-[width] duration-500 ease-out"
            style={{ width: `${mobileProgress}%` }}
          />
        </div>
      </div>
    </nav>
  );
}
